import { useEffect, useRef } from 'react';
import moment from 'moment-timezone';
import { useGetOrderFirstPageAPI, useGetOrdersMappingAPI } from '.';

import { TGetOrdersRes } from '../../../../types/responses/orders.type';
import { useAppDispatch, useAppSelector } from '../../../../hooks/useAppStore';
import {
  getDorChosenDate,
  getOrdersChosenDates,
} from '../../../../store/orders/selectors';
import { useGetOrdersCountAPI } from '../../../../hooks/api/useOrders';
import { useFetchRemainingOrders } from '../../Orders/useFetchRemainingOrders';
import { ordersActions } from '../../../../store/orders/slice';
import { useGetSheetsAPI } from '../../../../hooks/api/useSheets';
import { useGetLocationMetricsAPI } from '../../../../hooks/api/useIntegrations';
import { getIntegrations } from '../../../../store/settings/selectors';
import { getAuthenticateUser } from '../../../../store/authenticate/selectors';
import { TOrderMapping } from '../../../../types';

export function useOrdersByDates(
  setIsBtnDisabled: (val: boolean) => void,
  setIsFetchByDate: (val: boolean) => void,
) {
  const firstPageDataRef = useRef<TGetOrdersRes | null>(null);
  const dorChosenDate = useAppSelector(getDorChosenDate);
  const { shop } = useAppSelector(getAuthenticateUser);

  const ordersChosenDates = useAppSelector(getOrdersChosenDates);

  const dispatch = useAppDispatch();

  const integrations = useAppSelector(getIntegrations);

  const { refetch: refetchCount } = useGetOrdersCountAPI({
    isMainPage: false,
    dates: ordersChosenDates,
  });

  const { data: orderMappingsRes, refetch: refetchMappings } = useGetOrdersMappingAPI({
    isMainOrdersPage: false,
    dates: ordersChosenDates,
  }) as {
    data: {
      ordersMapping: Array<TOrderMapping>
    },
    refetch: any
  };

  const { data, refetch, isRefetching } = useGetOrderFirstPageAPI({
    isMainOrdersPage: false,
    dates: ordersChosenDates,
  });

  const { refetch: referchSheets } = useGetSheetsAPI({
    isMainSheetsPage: false,
    dates: ordersChosenDates,
    timezone: shop.shopTimezone,
  });

  const { refetch: referchMetrics } = useGetLocationMetricsAPI({
    isMainMetricsPage: false,
    dates: [
      moment.tz(dorChosenDate, shop?.shopTimezone).startOf('day').startOf('day'),
      moment.tz(dorChosenDate, shop?.shopTimezone).endOf('day').endOf('day'),
    ],
  });

  const { refetch: refetchRemainingOrders } = useFetchRemainingOrders(data as TGetOrdersRes, orderMappingsRes?.ordersMapping);

  useEffect(() => {
    if (firstPageDataRef.current?.hasNext && !isRefetching) {
      refetchRemainingOrders();
    }
  }, [isRefetching, refetchRemainingOrders, firstPageDataRef]);

  const getOrdersByDates = async () => {
    dispatch(ordersActions.setIsOrdersLoading(true));
    setIsFetchByDate(true);
    await referchSheets();
    await refetchCount();
    await refetchMappings();
    if (integrations?.Dor?.integrated) {
      await referchMetrics();
    }
    await refetch();
    setIsBtnDisabled(true);
  };

  return {
    getOrdersByDates,
  };
}
